/**
 * BRA-61 — client hook that downloads a Brain consulta as a PDF.
 *
 * Usage:
 *   const { exporting, exportSession, exportMessage, error } = useConsultaPdfExport(sessionId);
 *   await exportSession({ filters });
 *   await exportMessage(index, { filters });
 *
 * The server is authoritative: it reads the session from Supabase and
 * streams back `application/pdf`. The hook wraps the blob in an `<a download>`
 * click so the browser handles persistence (volatile, per the product spec).
 */

import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { authFetch } from "../../../services/authFetch";
import config from "../../../config";

export interface ConsultaExportFilters {
  tcFrom?: string;
  tcTo?: string;
  storyIds?: string[];
  sourceTypes?: ("log" | "trans" | "otf")[];
  excludeUserMessages?: boolean;
}

function _extractFilename(header: string | null, fallback: string): string {
  if (!header) return fallback;
  // Content-Disposition: attachment; filename="foo.pdf"
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  if (match && match[1]) return decodeURIComponent(match[1]);
  return fallback;
}

function _triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoking too eagerly cancels the download in Electron; defer until the
  // browser has had a chance to handle the click.
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export function useConsultaPdfExport(sessionId: string | null | undefined) {
  const { i18n } = useTranslation();
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runExport = useCallback(
    async (body: Record<string, unknown>) => {
      if (!sessionId) {
        setError("no-session");
        return false;
      }
      setExporting(true);
      setError(null);
      try {
        const res = await authFetch(
          `${config.API_URL}/api/discovery/sessions/${sessionId}/export-pdf`,
          {
            method: "POST",
            body: JSON.stringify({ ...body, locale: i18n.language || "es" }),
          },
        );
        if (!res.ok) {
          let msg = `HTTP ${res.status}`;
          try {
            const j = await res.json();
            if (j?.error) msg = j.error;
          } catch {
            /* ignore, keep generic */
          }
          setError(msg);
          return false;
        }
        const blob = await res.blob();
        const filename = _extractFilename(
          res.headers.get("Content-Disposition"),
          `consulta-${sessionId.slice(0, 8)}.pdf`,
        );
        _triggerDownload(blob, filename);
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        return false;
      } finally {
        setExporting(false);
      }
    },
    [sessionId, i18n.language],
  );

  const exportSession = useCallback(
    (filters: ConsultaExportFilters = {}) => runExport({ scope: "session", filters }),
    [runExport],
  );

  const exportMessage = useCallback(
    (messageIndex: number, filters: ConsultaExportFilters = {}) =>
      runExport({ scope: "message", messageIndex, filters }),
    [runExport],
  );

  return { exporting, error, exportSession, exportMessage };
}
